import { Action, Store } from '@openfin/predux';
import Fuse from 'fuse.js';

import { RootState } from '../State';
import { StoredNotification } from '../../model/StoredNotification';

export interface SearchState {
  searchMode: boolean;
  searching: boolean;
  query: string;
  ready: boolean;
  results: Fuse.FuseResult<StoredNotification>[];
}

export const initialSearchState: SearchState = {
  searchMode: false,
  searching: false,
  query: '',
  ready: false,
  results: [],
};

export const searchKeys: string[] = [
  'notification.title',
  'bodyText',
  'title',
  'notification.indicator.text',
];
declare global {
  interface Window {
    search: SearchWindow;
  }
}

interface SearchWindow {
  // Min length of the matching string length
  minLength: number;
  // Use the length of the query text and the min length
  useQueryLength: boolean;
  // If useQueryLength = true. query.length - queryLengthMinus
  queryLengthMinus: number;
  threshold: number;
  distance: number;
  // Expressions
  useExtendedSearch: boolean;
  logging: boolean;
}

if (typeof window !== 'undefined') {
  window.search = {
    minLength: 1,
    useQueryLength: true,
    queryLengthMinus: 0,
    threshold: 0.4,
    distance: 500,
    useExtendedSearch: false,
    logging: false,
  };
}

export class SearchNotifications extends Action<RootState> {
  public result!: Fuse.FuseResult<StoredNotification>[];

  constructor(public readonly text: string) {
    super();
  }

  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    const { notifications } = store.state;
    const titledNotifications = notifications.map((n) => {
      let note = n;
      if (note.notification.indicator) {
        note = {
          ...note,
          notification: {
            ...note.notification,
            indicator: {
              ...n.notification?.indicator,
              text: n.notification.indicator?.text || n.notification.indicator?.type,
            },
          },
        };
      }
      return note;
    });

    const fuse = new Fuse<StoredNotification>(titledNotifications, {
      keys: searchKeys,
      includeMatches: true,
      includeScore: true,
      useExtendedSearch: window.search.useExtendedSearch,
      minMatchCharLength: window.search.useQueryLength
        ? this.text.length - window.search.queryLengthMinus
        : window.search.minLength,
      threshold: window.search.threshold,
      distance: window.search.distance,
      sortFn: (a, b) => {
        // Sort by date if equal score
        if (a.score === b.score) {
          return (
            titledNotifications[b.idx].notification.date -
            titledNotifications[a.idx].notification.date
          );
        }
        // Sort by score ascending:
        // A perfect match is 0 so we want smallest first.
        return a.score - b.score;
      },
    });

    await store.dispatch(new SetSearching());

    new Promise(() => {
      this.result = fuse.search(this.text.trim(), { limit: 300 });
      if (window.search.logging) {
        console.log(this.result);
      }
      complete();
    });
  }

  public reduce(state: RootState): RootState {
    return {
      ...state,
      search: {
        ...state.search,
        searching: false,
        query: this.text,
        ready: true,
        results: this.result,
      },
    };
  }
}

export class ClearSearch extends Action<RootState> {
  public reduce(state: RootState): RootState {
    return {
      ...state,
      search: {
        ...state.search,
        query: '',
        searching: false,
        ready: false,
        results: [],
      },
    };
  }
}

export class SetSearching extends Action<RootState> {
  constructor() {
    super();
  }

  public reduce(state: RootState): RootState {
    return {
      ...state,
      search: { ...state.search, searching: true },
    };
  }
}

export class SetSearchMode extends Action<RootState> {
  constructor(public readonly mode: boolean) {
    super();
  }

  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    await store.dispatch(new ClearSearch());
    await complete();
  }

  public reduce(state: RootState): RootState {
    return {
      ...state,
      search: { ...state.search, searchMode: this.mode },
    };
  }
}
